import { HealthValues, OperationTypeValues, OperationValues, SyncValues, TagStatusValues } from '@admin/shared/data';
import { formControl } from '@al00x/forms';
import { Component, inject } from '@angular/core';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  formBuilder,
  ItemRecord,
} from '@controladad/ng-base';
import { ImageViewerComponent } from '@admin/shared';
import { HealthStatus, AssetAuditDetails, TagStatus, Operation, OperationType, PlanAssetAuditUpdate } from '@admin/core/models';
import { LocationApiService, PersonnelApiService, PropertyApiService } from '@admin/core';
import { personnelToString } from '@admin/shared/helpers';
import { Validators } from '@angular/forms';
import { BehaviorSubject, take } from 'rxjs';

export type AssetTrackingReportDialogData = {
  audit: AssetAuditDetails;
  editable?: boolean;
};
export type AssetTrackingReportDialogResult = boolean;

@Component({
  selector: 'app-asset-tracking-report-dialog',
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, ImageViewerComponent],
  templateUrl: './asset-tracking-report-dialog.component.html',
})
export class AssetTrackingReportDialogComponent extends CacBaseDialogComponent<
  AssetTrackingReportDialogData,
  AssetTrackingReportDialogResult
> {
  private readonly locationApi = inject(LocationApiService);
  private readonly personnelApi = inject(PersonnelApiService);
  private readonly propertyApi = inject(PropertyApiService);

  personelItems$ = new BehaviorSubject<ItemRecord<number>[]>([]);

  formBuilder = formBuilder({
    cols: 6,
    defaults: {
      colspan: 2,
    },
    inputs: {
      propertyName: {
        control: formControl<string>(undefined),
        label: 'نام دارایی',
      },
      propertyCode: {
        control: formControl<string>(undefined),
        label: 'کد دارایی',
      },
      managedDate: {
        control: formControl<string>(undefined),
        label: 'تاریخ ثبت',
        type: 'datetime',
      },
      locationName: {
        control: formControl<string>(undefined),
        label: 'مکان',
        colspan: 3,
        hidden: this.data.editable,
      },
      locationId: {
        control: formControl<number>(undefined, Validators.required),
        label: 'مکان',
        colspan: 3,
        type: 'select',
        items: this.locationApi.getLocationItems(),
        hidden: !this.data.editable,
      },
      locationNameBefore: {
        control: formControl<string>(undefined),
        label: 'مکان قبلی',
        colspan: 3,
      },
      mainPersonnel: {
        control: formControl<string | undefined>(undefined),
        label: 'تحویل گیرنده اصلی',
        colspan: 3,
        hidden: this.data.editable,
      },
      secondPersonnel: {
        control: formControl<string | undefined>(undefined),
        label: 'تحویل گیرنده دوم',
        colspan: 3,
        hidden: this.data.editable,
      },
      mainPersonnelId: {
        control: formControl<number | undefined>(undefined, Validators.required),
        label: 'تحویل گیرنده اصلی',
        type: 'select',
        colspan: 3,
        items: this.personelItems$,
        hidden: !this.data.editable,
      },
      secondPersonnelId: {
        control: formControl<number | undefined>(undefined),
        label: 'تحویل گیرنده دوم',
        type: 'select',
        colspan: 3,
        items: this.personelItems$,
        hidden: !this.data.editable,
      },
      mainPersonnelBefore: {
        control: formControl<string | undefined>(undefined),
        label: 'تحویل گیرنده اصلی قبلی',
        colspan: 3,
      },
      secondPersonnelBefore: {
        control: formControl<string | undefined>(undefined),
        label: 'تحویل گیرنده دوم قبلی',
        colspan: 3,
      },
      healthStatus: {
        control: formControl<HealthStatus>(undefined),
        label: 'وضعیت سلامت',
        type: 'select',
        items: HealthValues,
      },
      healthStatusBefore: {
        control: formControl<HealthStatus>(undefined),
        label: 'وضعیت قبلی سلامت',
        type: 'select',
        items: HealthValues,
      },
      tagStatus: {
        control: formControl<TagStatus>(undefined),
        label: 'وضعیت تگ',
        type: 'select',
        items: TagStatusValues,
      },
      tagStatusBefore: {
        control: formControl<TagStatus>(undefined),
        label: 'وضعیت قبلی تگ',
        type: 'select',
        items: TagStatusValues,
      },
      planName: {
        control: formControl<string>(undefined),
        label: 'برنامه',
      },
      userFullName: {
        control: formControl<string>(undefined),
        label: 'اموال گردان',
      },
      isSynced: {
        control: formControl<boolean>(undefined),
        label: 'وضعیت سینک',
        type: 'select',
        items: SyncValues,
      },
      operation: {
        type: 'select',
        control: formControl<Operation>(undefined),
        label: 'عملیات',
        items: OperationValues,
      },
      operationType: {
        type: 'select',
        control: formControl<OperationType>(undefined),
        label: 'نوع عملیات',
        items: OperationTypeValues,
      },
      auditDescription: {
        control: formControl<string>(undefined),
        label: 'توضیحات',
        type: 'textarea',
        colspan: 6,
      },
    },
    onInit: () => {
      this.formBuilder.setReadonly(true);
      if (this.data.editable) {
        this.formBuilder.setReadonly(false, 'locationId', 'mainPersonnelId', 'secondPersonnelId', 'auditDescription');
      }
    },
  });

  constructor() {
    super();
    this.personnelApi.getPersonnelItems().pipe(take(1)).subscribe(res => {
      this.personelItems$.next(res);
    });
    const details = this.data.audit;
    this.formBuilder.setValue({
      ...details,
      mainPersonnelId: details.personnel.at(0)?.id,
      secondPersonnelId: details.personnel.at(1)?.id,
      mainPersonnel: personnelToString(details.personnel.at(0)),
      secondPersonnel: personnelToString(details.personnel.at(1)),
      mainPersonnelBefore: personnelToString(details.personnelBefore.at(0)),
      secondPersonnelBefore: personnelToString(details.personnelBefore.at(1)),
    });
  }

  onSubmit(e: ButtonClickEvent) {
    const values = this.formBuilder.getValue();
    if (!values) return;
    const personnelIds = [];
    if (values.mainPersonnelId) {
      personnelIds.push(values.mainPersonnelId);
    }
    if (values.secondPersonnelId) {
      personnelIds.push(values.secondPersonnelId);
    }
    const model = {
      locationId: values.locationId,
      personnelIds,
      auditDescription: values.auditDescription,
    };
    if (model.personnelIds.length > 2) {
      snackbar$.error('تعداد تحویل گیرنده ها نمی تواند بیشتر از 2 نفر باشد');
      return;
    }
    this.propertyApi.updatePlanAssetAudit(this.data.audit.id, model).pipe(e.pipe()).subscribe(() => {
      this.submit(true);
    });
  }
}
